import React, { useState } from 'react';
import {
    CircularProgress,
    Grid2,
    TextField,
    Typography,
    Card,
    CardContent,
    Checkbox,
    FormControlLabel,
    Chip,
    Box,
    Alert,
    AlertTitle,
    Stepper,
    Step,
    StepLabel,
    LinearProgress,
    Paper,
    Divider,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    List,
    ListItem,
    ListItemText,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Button,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import PageWithTitle from '../../components/atom/PageWithTitle';
import BlockWithTitle from '../../components/atom/BlockWithTitle';
import CommonButton from '../../components/atom/CommonButton';
import { importer, importerDiscover } from '@openapi/importer/importer';
import {
    fhDirectConnect,
    fhDirectDiscoverItems,
    fhDirectManifest,
    fhDirectImport,
    fhDirectImportSeats,
    fhDirectVerify,
    fhDirectVerifyFix,
} from '@openapi/bookings/bookings';
import { addTicketOption } from '@openapi/ticket-options/ticket-options';
import { useGetShows } from '@openapi/shows/shows';
import useNotify from '../../hooks/useNotify';
import { formatCentsToUsd } from '../../helpers/formatCentsToUsd';

const STEPS = [
    'Discover',
    'Create Venue',
    'Connect',
    'Configure',
    'Manifest + Preview',
    'Import',
    'Verify',
];
const BATCH_SIZE = 10;
const DRY_RUN_BATCH_SIZE = 25;
const MAX_RETRIES = 3;
const RETRY_BASE_DELAY_MS = 2000;

function ImportVenuePage() {
    const { t } = useTranslation();
    const { notifyError, notifySuccess } = useNotify();
    const navigate = useNavigate();

    const [activeStep, setActiveStep] = useState(0);
    const [loading, setLoading] = useState(false);

    // Step 0: Discover
    const [slug, setSlug] = useState(process.env.REACT_APP_FH_SHORTNAME || '');
    const [discovered, setDiscovered] = useState(null);
    const [mergeEnabled, setMergeEnabled] = useState(false);
    const [mergeTitle, setMergeTitle] = useState('');
    const [selectedItemPks, setSelectedItemPks] = useState(new Set());

    // Step 2: Connect
    const [showId, setShowId] = useState('');
    const [fhEmail, setFhEmail] = useState(process.env.REACT_APP_FH_EMAIL || '');
    const [fhPassword, setFhPassword] = useState(process.env.REACT_APP_FH_PASSWORD || '');
    const [connectData, setConnectData] = useState(null);

    // Step 3: Discover Items
    const [discoverData, setDiscoverData] = useState(null);

    // Step 5: Manifest + Preview
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [manifestData, setManifestData] = useState(null);

    // Step 4: Configure — nested mapping: [{fhItemPk, fhItemName, ticketTypeId, customerTypes: [{name, price, ticketOptionId}]}]
    const [itemMappings, setItemMappings] = useState([]);
    const [sampleMode, setSampleMode] = useState(false);
    const [createMissingTimeslots, setCreateMissingTimeslots] = useState(true);
    const [skipCancelled, setSkipCancelled] = useState(false);
    const [skipRebooked, setSkipRebooked] = useState(false);
    const [resolveRebookedLinks, setResolveRebookedLinks] = useState(true);
    const [updateCommissionBillingType, setUpdateCommissionBillingType] = useState(true);
    const [createMissingPaymentMethods, setCreateMissingPaymentMethods] = useState(false);
    const [linkPaymentMethodsToGroups, setLinkPaymentMethodsToGroups] = useState(false);
    const [includeCancelledAvailabilities, setIncludeCancelledAvailabilities] = useState(true);
    const [skipDryRun, setSkipDryRun] = useState(false);
    // Seat map
    const [selectedSeatMapPk, setSelectedSeatMapPk] = useState('');
    const [zoneMappings, setZoneMappings] = useState({});
    const [seatsDryRun, setSeatsDryRun] = useState(true);
    const [seatsResult, setSeatsResult] = useState(null);

    // Step 5: Preview
    const [previewData, setPreviewData] = useState(null);
    const [manifestProgress, setManifestProgress] = useState(null); // { finished, total, label }
    const [dryRunProgress, setDryRunProgress] = useState(null); // { finished, total }

    // Step 6: Import
    const [importProgress, setImportProgress] = useState(null);
    const [importErrors, setImportErrors] = useState([]);

    // Verification
    const [verifyResult, setVerifyResult] = useState(null);
    const [verifyLoading, setVerifyLoading] = useState(false);
    const [fixLoading, setFixLoading] = useState(false);
    const [verifyIncludeAll, setVerifyIncludeAll] = useState(true);

    // Shows list for selection after venue creation
    const { data: showsData, refetch: refetchShows } = useGetShows(
        { page: 0, pageSize: 1000 },
        { query: { enabled: activeStep >= 1 } },
    );

    // ---- Helpers ----

    const credentials = { shortname: slug, fhEmail, fhPassword };

    const getImportableUuids = () => {
        if (!manifestData?.bookings) return [];
        // alreadyImported is intentionally NOT a filter — the import endpoint
        // overwrites existing bookings on re-run so users can rerun corrections.
        return manifestData.bookings
            .filter((b) => !skipCancelled || !b.isCancelled || b.isRebooked)
            .filter((b) => !skipRebooked || !b.isRebooked)
            .map((b) => b.uuid);
    };

    const buildTicketMapping = () =>
        itemMappings.map((im) => ({
            fhItemPk: im.fhItemPk,
            ticketTypeId: im.ticketTypeId ? parseInt(im.ticketTypeId) : null,
            customerTypes: (im.customerTypes || []).map((ct) => ({
                name: ct.name,
                pk: ct.pk || null,
                ticketOptionId: ct.ticketOptionId ? parseInt(ct.ticketOptionId) : null,
            })),
        }));

    const toggleItem = (pk) => {
        setSelectedItemPks((prev) => {
            const next = new Set(prev);
            if (next.has(pk)) next.delete(pk);
            else next.add(pk);
            return next;
        });
    };

    const selectedSeatMap = connectData?.fhSeatMaps?.find(
        (sm) => sm.pk === parseInt(selectedSeatMapPk),
    );

    // ---- Step handlers ----

    const handleDiscover = async () => {
        if (!slug) {
            notifyError(t('common.required'));
            return;
        }
        setLoading(true);
        try {
            const data = await importerDiscover(slug);
            setDiscovered(data);
            setSelectedItemPks(new Set((data.items || []).map((item) => item.pk)));
            if (data.venue?.name) setMergeTitle(data.venue.name);
            setActiveStep(1);
        } catch (err) {
            notifyError(err);
        } finally {
            setLoading(false);
        }
    };

    const handleCreateVenue = async () => {
        setLoading(true);
        try {
            let body = {};
            if (mergeEnabled && selectedItemPks.size > 0) {
                body = {
                    showConfigs: [
                        {
                            title: mergeTitle || discovered?.venue?.name || slug,
                            itemPks: Array.from(selectedItemPks),
                        },
                    ],
                };
            }
            // Fetch current shows so we can detect the newly created one
            const beforeResult = await refetchShows();
            const showIdsBefore = new Set((beforeResult.data?.shows || []).map((s) => s.id));
            await importer(slug, body);
            notifySuccess('Venue created successfully');
            const afterResult = await refetchShows();
            // Pre-select the newly created show
            const newShow = (afterResult.data?.shows || []).find((s) => !showIdsBefore.has(s.id));
            if (newShow) setShowId(newShow.id);
            setActiveStep(2);
        } catch (err) {
            notifyError(err);
        } finally {
            setLoading(false);
        }
    };

    const handleSkipCreate = async () => {
        await refetchShows();
        setActiveStep(2);
    };

    const handleConnect = async () => {
        if (!showId || !fhEmail || !fhPassword) {
            notifyError('Please select a show and fill in FH credentials');
            return;
        }
        setLoading(true);
        try {
            const connectResponse = await fhDirectConnect({
                showId: parseInt(showId),
                ...credentials,
            });
            setConnectData(connectResponse);

            // Auto-discover items + customer types
            const data = await fhDirectDiscoverItems({
                showId: parseInt(showId),
                ...credentials,
            });
            setDiscoverData(data);

            const ticketTypes = data.ticketTypes || connectResponse.ticketTypes || [];
            const ticketOptions = data.ticketOptions || connectResponse.ticketOptions || [];

            const newItemMappings = (data.fhItems || []).map((fhItem) => {
                const matchedTT = ticketTypes.find(
                    (tt) => tt.name?.toLowerCase() === fhItem.name?.toLowerCase(),
                );
                const customerTypes = (fhItem.customerTypeRates || []).map((rate) => {
                    const matchedTO = ticketOptions.find(
                        (to) => to.name?.toLowerCase() === rate.name?.toLowerCase(),
                    );
                    return {
                        name: rate.name,
                        pk: rate.pk,
                        price: rate.price,
                        ticketOptionId: matchedTO?.id || '',
                    };
                });
                return {
                    fhItemPk: fhItem.id,
                    fhItemName: fhItem.name,
                    ticketTypeId: matchedTT?.id || '',
                    customerTypes,
                };
            });
            setItemMappings(newItemMappings);
            setActiveStep(3);
        } catch (err) {
            notifyError(err);
        } finally {
            setLoading(false);
        }
    };

    const getMonthRanges = (from, to) => {
        const toYmd = (d) => {
            const y = d.getFullYear();
            const m = String(d.getMonth() + 1).padStart(2, '0');
            const day = String(d.getDate()).padStart(2, '0');
            return `${y}-${m}-${day}`;
        };
        const ranges = [];
        let cursor = new Date(from + 'T00:00:00');
        const end = new Date(to + 'T00:00:00');
        while (cursor <= end) {
            const monthStart = toYmd(cursor);
            const monthEnd = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0);
            const rangeEnd = monthEnd > end ? to : toYmd(monthEnd);
            ranges.push({ from: monthStart, to: rangeEnd });
            cursor = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1);
        }
        return ranges;
    };

    const handleManifestAndPreview = async () => {
        if (!dateFrom || !dateTo) {
            notifyError('Please select a date range');
            return;
        }
        setLoading(true);
        setManifestData(null);
        setPreviewData(null);
        setManifestProgress(null);
        setDryRunProgress(null);
        try {
            // Split manifest fetch into monthly chunks
            const months = getMonthRanges(dateFrom, dateTo);
            const mergedManifest = {
                totalBookings: 0,
                alreadyImportedCount: 0,
                cancelledCount: 0,
                rebookedCount: 0,
                bookings: [],
                dates: [],
            };
            setManifestProgress({ finished: 0, total: months.length, label: '' });

            for (let mi = 0; mi < months.length; mi++) {
                const { from, to } = months[mi];
                setManifestProgress({
                    finished: mi,
                    total: months.length,
                    label: `${from} - ${to}`,
                });
                const data = await fhDirectManifest({
                    showId: parseInt(showId),
                    ...credentials,
                    fhItemIds: itemMappings.map((im) => im.fhItemPk).filter(Boolean),
                    dateFrom: from,
                    dateTo: to,
                    includeCancelledAvailabilities,
                });
                mergedManifest.totalBookings += data.totalBookings || 0;
                mergedManifest.alreadyImportedCount += data.alreadyImportedCount || 0;
                mergedManifest.cancelledCount += data.cancelledCount || 0;
                mergedManifest.rebookedCount += data.rebookedCount || 0;
                mergedManifest.bookings.push(...(data.bookings || []));
                mergedManifest.dates.push(...(data.dates || []));
            }
            setManifestProgress({ finished: months.length, total: months.length, label: 'Done' });
            setManifestData(mergedManifest);

            // Auto dry-run (batched to avoid timeout)
            // alreadyImported is intentionally NOT a filter — see getImportableUuids.
            const uuids = mergedManifest.bookings
                .filter((b) => !skipCancelled || !b.isCancelled || b.isRebooked)
                .filter((b) => !skipRebooked || !b.isRebooked)
                .map((b) => b.uuid);

            if (uuids.length > 0 && !skipDryRun) {
                const mergedPreview = {
                    finished: 0,
                    total: uuids.length,
                    imported: 0,
                    skipped: 0,
                    errors: [],
                    warnings: [],
                    preview: {
                        commissionGroups: [],
                        agents: [],
                        timeslotsToCreate: [],
                        financialSummary: {
                            totalRevenue: 0,
                            totalCommission: 0,
                            totalProcessingFees: 0,
                            totalCollected: 0,
                            totalCollectedGross: 0,
                            totalRevenueCollected: 0,
                            totalReceivableFromAffiliate: 0,
                            totalTax: 0,
                            totalAmountDue: 0,
                            netRevenue: 0,
                        },
                    },
                };
                const seenCommissionGroups = new Set();
                const seenAgents = new Set();
                setDryRunProgress({ finished: 0, total: uuids.length });

                for (let i = 0; i < uuids.length; i += DRY_RUN_BATCH_SIZE) {
                    const batch = uuids.slice(i, i + DRY_RUN_BATCH_SIZE);
                    const result = await fhDirectImport({
                        showId: parseInt(showId),
                        ...credentials,
                        bookingUuids: batch,
                        ticketmapping: buildTicketMapping(),
                        dryRun: true,
                        sampleMode,
                        createMissingTimeslots,
                        skipCancelled,
                        skipRebooked,
                        resolveRebookedLinks,
                        updateCommissionBillingType,
                        createMissingPaymentMethods,
                        linkPaymentMethodsToGroups,
                    });

                    mergedPreview.finished += result.finished || 0;
                    mergedPreview.imported += result.imported || 0;
                    mergedPreview.skipped += result.skipped || 0;
                    mergedPreview.errors.push(...(result.errors || []));
                    mergedPreview.warnings.push(...(result.warnings || []));

                    const p = result.preview || {};
                    for (const cg of p.commissionGroups || []) {
                        if (!seenCommissionGroups.has(cg.name)) {
                            seenCommissionGroups.add(cg.name);
                            mergedPreview.preview.commissionGroups.push(cg);
                        }
                    }
                    for (const ag of p.agents || []) {
                        if (!seenAgents.has(ag.name)) {
                            seenAgents.add(ag.name);
                            mergedPreview.preview.agents.push(ag);
                        }
                    }
                    for (const ts of p.timeslotsToCreate || []) {
                        if (!mergedPreview.preview.timeslotsToCreate.includes(ts)) {
                            mergedPreview.preview.timeslotsToCreate.push(ts);
                        }
                    }
                    const fs = p.financialSummary || {};
                    mergedPreview.preview.financialSummary.totalRevenue += fs.totalRevenue || 0;
                    mergedPreview.preview.financialSummary.totalCommission +=
                        fs.totalCommission || 0;
                    mergedPreview.preview.financialSummary.totalProcessingFees +=
                        fs.totalProcessingFees || 0;
                    mergedPreview.preview.financialSummary.totalCollected += fs.totalCollected || 0;
                    mergedPreview.preview.financialSummary.totalCollectedGross +=
                        fs.totalCollectedGross || 0;
                    mergedPreview.preview.financialSummary.totalRevenueCollected +=
                        fs.totalRevenueCollected || 0;
                    mergedPreview.preview.financialSummary.totalReceivableFromAffiliate +=
                        fs.totalReceivableFromAffiliate || 0;
                    mergedPreview.preview.financialSummary.totalTax += fs.totalTax || 0;
                    mergedPreview.preview.financialSummary.totalAmountDue += fs.totalAmountDue || 0;
                    mergedPreview.preview.financialSummary.netRevenue += fs.netRevenue || 0;

                    setDryRunProgress({
                        finished: Math.min(i + batch.length, uuids.length),
                        total: uuids.length,
                    });
                }

                setPreviewData(mergedPreview);
            }

            setActiveStep(4);
        } catch (err) {
            notifyError(err);
        } finally {
            setLoading(false);
            setManifestProgress(null);
            setDryRunProgress(null);
        }
    };

    const handleImportSeats = async () => {
        if (!selectedSeatMapPk) return;
        setLoading(true);
        try {
            const result = await fhDirectImportSeats({
                showId: parseInt(showId),
                ...credentials,
                fhSeatMapPk: parseInt(selectedSeatMapPk),
                zoneMappings: Object.entries(zoneMappings)
                    .filter(([, ticketTypeId]) => ticketTypeId)
                    .map(([fhZonePk, ticketTypeId]) => ({
                        fhZonePk: parseInt(fhZonePk),
                        ticketTypeId: parseInt(ticketTypeId),
                    })),
                dryRun: seatsDryRun,
            });
            setSeatsResult(result);
            if (!seatsDryRun) {
                notifySuccess(`Imported ${result.seatsCreated} seats`);
            }
        } catch (err) {
            notifyError(err);
        } finally {
            setLoading(false);
        }
    };

    const handleExecuteImport = async () => {
        const allUuids = getImportableUuids();
        if (allUuids.length === 0) return;

        setLoading(true);
        setImportProgress({ finished: 0, total: allUuids.length, imported: 0 });
        setImportErrors([]);
        setActiveStep(5);

        let totalImported = 0;
        const allErrors = [];

        for (let i = 0; i < allUuids.length; i += BATCH_SIZE) {
            const batch = allUuids.slice(i, i + BATCH_SIZE);
            let response = null;

            for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
                try {
                    response = await fhDirectImport({
                        showId: parseInt(showId),
                        ...credentials,
                        bookingUuids: batch,
                        ticketmapping: buildTicketMapping(),
                        dryRun: false,
                        sampleMode,
                        createMissingTimeslots,
                        skipCancelled,
                        skipRebooked,
                        resolveRebookedLinks,
                        updateCommissionBillingType,
                        createMissingPaymentMethods,
                        linkPaymentMethodsToGroups,
                    });
                    break;
                } catch (err) {
                    if (attempt < MAX_RETRIES) {
                        const delay = RETRY_BASE_DELAY_MS * Math.pow(2, attempt - 1);
                        console.warn(
                            `[Import] Batch at offset ${i} failed (attempt ${attempt}/${MAX_RETRIES}), retrying in ${delay}ms...`,
                            err,
                        );
                        await new Promise((resolve) => setTimeout(resolve, delay));
                    } else {
                        console.error(
                            `[Import] Batch at offset ${i} failed after ${MAX_RETRIES} attempts`,
                            err,
                        );
                        allErrors.push(
                            ...batch.map((uuid) => ({
                                bookingId: uuid,
                                error: `Batch request failed after ${MAX_RETRIES} retries: ${err?.message || err}`,
                            })),
                        );
                    }
                }
            }

            if (response) {
                totalImported += response.imported || 0;
                allErrors.push(...(response.errors || []));
            }
            setImportProgress({
                finished: i + batch.length,
                total: allUuids.length,
                imported: totalImported,
            });
            setImportErrors([...allErrors]);
        }

        if (allErrors.length > 0) {
            notifyError(`Import completed: ${totalImported} imported, ${allErrors.length} errors`);
        } else {
            notifySuccess(`Successfully imported ${totalImported} booking(s)`);
        }
        setLoading(false);
    };

    // ---- Render helpers ----

    const renderStepNav = (backStep, onNext, nextLabel, nextDisabled) => (
        <Box
            sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mt: 3 }}
        >
            {backStep !== null && (
                <CommonButton
                    onClick={() => setActiveStep(backStep)}
                    label="Back"
                    buttonType="secondary"
                />
            )}
            {onNext && (
                <CommonButton
                    onClick={onNext}
                    disabled={nextDisabled || loading}
                    label={loading ? 'Loading...' : nextLabel}
                    buttonType="primary"
                />
            )}
        </Box>
    );

    const fmtUsd = (v) => formatCentsToUsd(v || 0);
    const importableUuids = getImportableUuids();

    // ---- Render ----

    return (
        <Box sx={{ '& .MuiCardContent-root, & .MuiAccordionDetails-root': { textAlign: 'left' } }}>
            <PageWithTitle title={t('venues.importTitle')}>
                <Grid2 size={{ xs: 12 }} sx={{ overflowX: 'auto' }}>
                    <Stepper
                        activeStep={activeStep}
                        alternativeLabel
                        sx={{ minWidth: { xs: 560, md: 'auto' } }}
                    >
                        {STEPS.map((label) => (
                            <Step key={label}>
                                <StepLabel>{label}</StepLabel>
                            </Step>
                        ))}
                    </Stepper>
                </Grid2>

                {/* Step 0: Discover */}
                {activeStep === 0 && (
                    <Grid2 size={{ xs: 12 }}>
                        <BlockWithTitle title="Enter FareHarbor Shortname">
                            {loading ? (
                                <Loader />
                            ) : (
                                <>
                                    <Grid2 size={{ xs: 12 }}>
                                        <TextField
                                            fullWidth
                                            label={t('venues.columns.slug')}
                                            value={slug}
                                            onChange={(e) => setSlug(e.target.value)}
                                            placeholder=""
                                        />
                                    </Grid2>
                                    <Grid2 size={{ xs: 12 }}>
                                        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                                            <CommonButton
                                                onClick={() => navigate('/venues')}
                                                label={t('common.cancel')}
                                                buttonType="secondary"
                                            />
                                            <CommonButton
                                                onClick={handleDiscover}
                                                label="Discover"
                                                buttonType="primary"
                                            />
                                            <CommonButton
                                                onClick={handleSkipCreate}
                                                label="Skip to Connect (venue exists)"
                                                buttonType="secondary"
                                            />
                                        </Box>
                                    </Grid2>
                                </>
                            )}
                        </BlockWithTitle>
                    </Grid2>
                )}

                {/* Step 1: Configure venue + shows, then create */}
                {activeStep === 1 && discovered && (
                    <Grid2 size={{ xs: 12 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h5" gutterBottom>
                                        Venue: {discovered.venue?.name}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" gutterBottom>
                                        {discovered.venue?.url}
                                    </Typography>

                                    <Divider sx={{ my: 2 }} />

                                    <Typography variant="h5" gutterBottom>
                                        FareHarbor Items ({discovered.items?.length || 0})
                                    </Typography>

                                    {discovered.items?.map((item) => (
                                        <Card
                                            key={item.pk}
                                            variant="outlined"
                                            sx={{ mb: 1, p: 1.5 }}
                                        >
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                }}
                                            >
                                                <Box>
                                                    <Typography variant="subtitle1">
                                                        {item.title}
                                                    </Typography>
                                                    <Typography
                                                        variant="body2"
                                                        color="text.secondary"
                                                    >
                                                        {item.timeslotCount} timeslots &bull;{' '}
                                                        {item.location}
                                                    </Typography>
                                                    <Box
                                                        sx={{
                                                            mt: 0.5,
                                                            display: 'flex',
                                                            gap: 0.5,
                                                            flexWrap: 'wrap',
                                                        }}
                                                    >
                                                        {item.ticketTypes
                                                            ?.filter((tt) => tt.showInList)
                                                            .map((tt, i) => (
                                                                <Chip
                                                                    key={i}
                                                                    label={`${tt.name}: $${(tt.price / 100).toFixed(2)}`}
                                                                    size="small"
                                                                    variant="outlined"
                                                                />
                                                            ))}
                                                    </Box>
                                                </Box>
                                                {mergeEnabled && (
                                                    <Checkbox
                                                        checked={selectedItemPks.has(item.pk)}
                                                        onChange={() => toggleItem(item.pk)}
                                                    />
                                                )}
                                            </Box>
                                        </Card>
                                    ))}

                                    {discovered.items?.length > 1 && (
                                        <Box sx={{ mt: 2 }}>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={mergeEnabled}
                                                        onChange={(e) =>
                                                            setMergeEnabled(e.target.checked)
                                                        }
                                                    />
                                                }
                                                label="Merge selected items into one show (items become ticket types)"
                                            />
                                            {mergeEnabled && (
                                                <TextField
                                                    fullWidth
                                                    label="Show title"
                                                    value={mergeTitle}
                                                    onChange={(e) => setMergeTitle(e.target.value)}
                                                    sx={{ mt: 1 }}
                                                    size="small"
                                                />
                                            )}
                                            {mergeEnabled && (
                                                <Alert severity="info" sx={{ mt: 1 }}>
                                                    {selectedItemPks.size} item(s) will be merged.
                                                    Each item becomes a ticket type. Customer types
                                                    become shared ticket options.
                                                </Alert>
                                            )}
                                        </Box>
                                    )}
                                </CardContent>
                            </Card>
                            {renderStepNav(
                                0,
                                handleCreateVenue,
                                loading ? 'Creating...' : 'Create Venue & Shows',
                                loading,
                            )}
                        </Box>
                    </Grid2>
                )}

                {/* Step 2: Connect FH Agent */}
                {activeStep === 2 && (
                    <Grid2 size={{ xs: 12 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h5" gutterBottom>
                                        Connect FareHarbor Agent
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ mb: 2 }}
                                    >
                                        Select the Fireknife show to import into, then enter your
                                        FareHarbor credentials.
                                    </Typography>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 2,
                                        }}
                                    >
                                        <FormControl fullWidth>
                                            <InputLabel>Select Show</InputLabel>
                                            <Select
                                                value={showId}
                                                label="Select Show"
                                                onChange={(e) => setShowId(e.target.value)}
                                                sx={{ textAlign: 'left' }}
                                            >
                                                {showsData?.shows?.map((show) => (
                                                    <MenuItem key={show.id} value={show.id}>
                                                        {show.title} (ID: {show.id})
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                        <TextField
                                            fullWidth
                                            label="FH Shortname"
                                            value={slug}
                                            onChange={(e) => setSlug(e.target.value)}
                                            placeholder="wailuanuiluau"
                                        />
                                        <TextField
                                            fullWidth
                                            label="User"
                                            value={fhEmail}
                                            onChange={(e) => setFhEmail(e.target.value)}
                                        />
                                        <TextField
                                            fullWidth
                                            label="Password"
                                            type="password"
                                            value={fhPassword}
                                            onChange={(e) => setFhPassword(e.target.value)}
                                        />
                                    </Box>
                                </CardContent>
                            </Card>
                            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                                <CommonButton
                                    onClick={() => setActiveStep(0)}
                                    label="Back"
                                    buttonType="secondary"
                                />
                                <CommonButton
                                    onClick={() => navigate('/venues')}
                                    label="Skip (finish)"
                                    buttonType="secondary"
                                />
                                <CommonButton
                                    onClick={handleConnect}
                                    disabled={loading}
                                    label={loading ? 'Connecting...' : 'Connect & Continue'}
                                    buttonType="primary"
                                />
                                {showId && (
                                    <CommonButton
                                        onClick={() => setActiveStep(6)}
                                        label="Skip to Verify"
                                        buttonType="secondary"
                                    />
                                )}
                            </Box>
                        </Box>
                    </Grid2>
                )}

                {/* Step 3: Configure — ticket mapping, date range, seat maps, options */}
                {activeStep === 3 && (
                    <Grid2 size={{ xs: 12 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            {/* Ticket type mapping — nested per-item */}
                            <Card>
                                <CardContent>
                                    <Typography variant="h5" gutterBottom>
                                        Ticket Type Mapping
                                    </Typography>
                                    <Alert severity="info" sx={{ mb: 2 }}>
                                        Map each FareHarbor item to a ticket type, and its customer
                                        types to ticket options.
                                    </Alert>
                                    <TableContainer component={Paper} variant="outlined">
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>FH Item / Customer Type</TableCell>
                                                    <TableCell>Map To</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {itemMappings.map((im, imIdx) => (
                                                    <React.Fragment key={`item-${im.fhItemPk}`}>
                                                        {/* Item row → Ticket Type */}
                                                        <TableRow sx={{ bgcolor: 'action.hover' }}>
                                                            <TableCell>
                                                                <Typography variant="subtitle2">
                                                                    {im.fhItemName}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell>
                                                                <FormControl fullWidth size="small">
                                                                    <Select
                                                                        value={im.ticketTypeId}
                                                                        onChange={(e) => {
                                                                            const updated = [
                                                                                ...itemMappings,
                                                                            ];
                                                                            updated[imIdx] = {
                                                                                ...updated[imIdx],
                                                                                ticketTypeId:
                                                                                    e.target.value,
                                                                            };
                                                                            setItemMappings(
                                                                                updated,
                                                                            );
                                                                        }}
                                                                        displayEmpty
                                                                    >
                                                                        <MenuItem value="">
                                                                            <em>
                                                                                Select ticket type
                                                                            </em>
                                                                        </MenuItem>
                                                                        {(
                                                                            discoverData?.ticketTypes ||
                                                                            connectData?.ticketTypes
                                                                        )?.map((tt) => (
                                                                            <MenuItem
                                                                                key={tt.id}
                                                                                value={tt.id}
                                                                            >
                                                                                {tt.name}
                                                                            </MenuItem>
                                                                        ))}
                                                                    </Select>
                                                                </FormControl>
                                                            </TableCell>
                                                        </TableRow>
                                                        {/* Customer type rows → Ticket Options */}
                                                        {im.customerTypes.map((ct, ctIdx) => (
                                                            <TableRow
                                                                key={`ct-${im.fhItemPk}-${ct.name}`}
                                                            >
                                                                <TableCell sx={{ pl: 4 }}>
                                                                    <Typography variant="body2">
                                                                        {ct.name}
                                                                    </Typography>
                                                                    {ct.price > 0 && (
                                                                        <Typography
                                                                            variant="caption"
                                                                            color="text.secondary"
                                                                        >
                                                                            $
                                                                            {(
                                                                                ct.price / 100
                                                                            ).toFixed(2)}
                                                                        </Typography>
                                                                    )}
                                                                </TableCell>
                                                                <TableCell>
                                                                    <FormControl
                                                                        fullWidth
                                                                        size="small"
                                                                    >
                                                                        <Select
                                                                            value={
                                                                                ct.ticketOptionId
                                                                            }
                                                                            onChange={(e) => {
                                                                                const updated = [
                                                                                    ...itemMappings,
                                                                                ];
                                                                                const cts = [
                                                                                    ...updated[
                                                                                        imIdx
                                                                                    ].customerTypes,
                                                                                ];
                                                                                cts[ctIdx] = {
                                                                                    ...cts[ctIdx],
                                                                                    ticketOptionId:
                                                                                        e.target
                                                                                            .value,
                                                                                };
                                                                                updated[imIdx] = {
                                                                                    ...updated[
                                                                                        imIdx
                                                                                    ],
                                                                                    customerTypes:
                                                                                        cts,
                                                                                };
                                                                                setItemMappings(
                                                                                    updated,
                                                                                );
                                                                            }}
                                                                            displayEmpty
                                                                        >
                                                                            <MenuItem value="">
                                                                                <em>
                                                                                    Select ticket
                                                                                    option
                                                                                </em>
                                                                            </MenuItem>
                                                                            {(
                                                                                discoverData?.ticketOptions ||
                                                                                connectData?.ticketOptions
                                                                            )?.map((to) => (
                                                                                <MenuItem
                                                                                    key={to.id}
                                                                                    value={to.id}
                                                                                >
                                                                                    {to.name}
                                                                                </MenuItem>
                                                                            ))}
                                                                        </Select>
                                                                    </FormControl>
                                                                    {!ct.ticketOptionId &&
                                                                        showId && (
                                                                            <Button
                                                                                size="small"
                                                                                variant="outlined"
                                                                                sx={{
                                                                                    whiteSpace:
                                                                                        'nowrap',
                                                                                    minWidth:
                                                                                        'auto',
                                                                                }}
                                                                                onClick={async () => {
                                                                                    try {
                                                                                        const result =
                                                                                            await addTicketOption(
                                                                                                {
                                                                                                    name: ct.name,
                                                                                                    showId: parseInt(
                                                                                                        showId,
                                                                                                    ),
                                                                                                },
                                                                                            );
                                                                                        const newOpt =
                                                                                            {
                                                                                                id: result.id,
                                                                                                name:
                                                                                                    result.name ||
                                                                                                    ct.name,
                                                                                            };
                                                                                        setDiscoverData(
                                                                                            (
                                                                                                prev,
                                                                                            ) =>
                                                                                                prev
                                                                                                    ? {
                                                                                                          ...prev,
                                                                                                          ticketOptions:
                                                                                                              [
                                                                                                                  ...(prev.ticketOptions ||
                                                                                                                      []),
                                                                                                                  newOpt,
                                                                                                              ],
                                                                                                      }
                                                                                                    : prev,
                                                                                        );
                                                                                        const updated =
                                                                                            [
                                                                                                ...itemMappings,
                                                                                            ];
                                                                                        const cts =
                                                                                            [
                                                                                                ...updated[
                                                                                                    imIdx
                                                                                                ]
                                                                                                    .customerTypes,
                                                                                            ];
                                                                                        cts[ctIdx] =
                                                                                            {
                                                                                                ...cts[
                                                                                                    ctIdx
                                                                                                ],
                                                                                                ticketOptionId:
                                                                                                    newOpt.id,
                                                                                            };
                                                                                        updated[
                                                                                            imIdx
                                                                                        ] = {
                                                                                            ...updated[
                                                                                                imIdx
                                                                                            ],
                                                                                            customerTypes:
                                                                                                cts,
                                                                                        };
                                                                                        setItemMappings(
                                                                                            updated,
                                                                                        );
                                                                                    } catch {
                                                                                        // silently fail
                                                                                    }
                                                                                }}
                                                                            >
                                                                                Create &quot;
                                                                                {ct.name}&quot;
                                                                            </Button>
                                                                        )}
                                                                </TableCell>
                                                            </TableRow>
                                                        ))}
                                                    </React.Fragment>
                                                ))}
                                                {itemMappings.length === 0 && (
                                                    <TableRow>
                                                        <TableCell colSpan={2}>
                                                            <Typography
                                                                variant="body2"
                                                                color="text.secondary"
                                                            >
                                                                No FH items found. Customer types
                                                                will be discovered from manifest
                                                                bookings.
                                                            </Typography>
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </CardContent>
                            </Card>

                            {/* Seat map import (optional) */}
                            {connectData?.fhSeatMaps?.length > 0 && (
                                <Accordion>
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                        <Typography variant="h5">
                                            Import Seat Map (optional) —{' '}
                                            {connectData.fhSeatMaps.length} available
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: 2,
                                            }}
                                        >
                                            <FormControl fullWidth>
                                                <InputLabel>Seat Map</InputLabel>
                                                <Select
                                                    value={selectedSeatMapPk}
                                                    label="Seat Map"
                                                    onChange={(e) => {
                                                        setSelectedSeatMapPk(e.target.value);
                                                        setSeatsResult(null);
                                                        setZoneMappings({});
                                                    }}
                                                    sx={{ textAlign: 'left' }}
                                                >
                                                    <MenuItem value="">
                                                        <em>None</em>
                                                    </MenuItem>
                                                    {connectData.fhSeatMaps.map((sm) => (
                                                        <MenuItem key={sm.pk} value={sm.pk}>
                                                            {sm.name} ({sm.seatCount} seats,{' '}
                                                            {sm.zoneCount} zones)
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>

                                            {selectedSeatMap?.zones?.length > 0 && (
                                                <>
                                                    <Typography variant="subtitle1">
                                                        Zone to Ticket Type Mapping
                                                    </Typography>
                                                    <TableContainer
                                                        component={Paper}
                                                        variant="outlined"
                                                    >
                                                        <Table size="small">
                                                            <TableHead>
                                                                <TableRow>
                                                                    <TableCell>FH Zone</TableCell>
                                                                    <TableCell>
                                                                        Ticket Type
                                                                    </TableCell>
                                                                </TableRow>
                                                            </TableHead>
                                                            <TableBody>
                                                                {selectedSeatMap.zones.map(
                                                                    (zone) => {
                                                                        const ticketTypes = [];
                                                                        const seen = new Set();
                                                                        connectData.showItems?.forEach(
                                                                            (si) => {
                                                                                if (
                                                                                    !seen.has(
                                                                                        si.ticketTypeId,
                                                                                    )
                                                                                ) {
                                                                                    seen.add(
                                                                                        si.ticketTypeId,
                                                                                    );
                                                                                    ticketTypes.push(
                                                                                        {
                                                                                            id: si.ticketTypeId,
                                                                                            name: si.ticketTypeName,
                                                                                        },
                                                                                    );
                                                                                }
                                                                            },
                                                                        );
                                                                        return (
                                                                            <TableRow key={zone.pk}>
                                                                                <TableCell>
                                                                                    {zone.name} (pk:{' '}
                                                                                    {zone.pk})
                                                                                </TableCell>
                                                                                <TableCell>
                                                                                    <FormControl
                                                                                        fullWidth
                                                                                        size="small"
                                                                                    >
                                                                                        <Select
                                                                                            value={
                                                                                                zoneMappings[
                                                                                                    zone
                                                                                                        .pk
                                                                                                ] ||
                                                                                                ''
                                                                                            }
                                                                                            onChange={(
                                                                                                e,
                                                                                            ) =>
                                                                                                setZoneMappings(
                                                                                                    {
                                                                                                        ...zoneMappings,
                                                                                                        [zone.pk]:
                                                                                                            e
                                                                                                                .target
                                                                                                                .value,
                                                                                                    },
                                                                                                )
                                                                                            }
                                                                                            displayEmpty
                                                                                        >
                                                                                            <MenuItem value="">
                                                                                                <em>
                                                                                                    Unmapped
                                                                                                </em>
                                                                                            </MenuItem>
                                                                                            {ticketTypes.map(
                                                                                                (
                                                                                                    tt,
                                                                                                ) => (
                                                                                                    <MenuItem
                                                                                                        key={
                                                                                                            tt.id
                                                                                                        }
                                                                                                        value={
                                                                                                            tt.id
                                                                                                        }
                                                                                                    >
                                                                                                        {
                                                                                                            tt.name
                                                                                                        }
                                                                                                    </MenuItem>
                                                                                                ),
                                                                                            )}
                                                                                        </Select>
                                                                                    </FormControl>
                                                                                </TableCell>
                                                                            </TableRow>
                                                                        );
                                                                    },
                                                                )}
                                                            </TableBody>
                                                        </Table>
                                                    </TableContainer>
                                                </>
                                            )}

                                            {selectedSeatMapPk && (
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        flexDirection: {
                                                            xs: 'column',
                                                            sm: 'row',
                                                        },
                                                        gap: 2,
                                                        alignItems: {
                                                            xs: 'stretch',
                                                            sm: 'center',
                                                        },
                                                    }}
                                                >
                                                    <FormControlLabel
                                                        control={
                                                            <Checkbox
                                                                checked={seatsDryRun}
                                                                onChange={(e) =>
                                                                    setSeatsDryRun(e.target.checked)
                                                                }
                                                            />
                                                        }
                                                        label="Dry run (preview only)"
                                                    />
                                                    <CommonButton
                                                        onClick={handleImportSeats}
                                                        disabled={loading}
                                                        label={
                                                            loading
                                                                ? 'Importing...'
                                                                : seatsDryRun
                                                                  ? 'Preview Seat Import'
                                                                  : 'Import Seats'
                                                        }
                                                        buttonType="primary"
                                                    />
                                                </Box>
                                            )}

                                            {seatsResult && (
                                                <Alert
                                                    severity={
                                                        seatsResult.dryRun ? 'info' : 'success'
                                                    }
                                                    sx={{ textAlign: 'left' }}
                                                >
                                                    <AlertTitle sx={{ fontWeight: 'bold' }}>
                                                        {seatsResult.dryRun
                                                            ? 'Seat Import Preview'
                                                            : 'Seats Imported'}
                                                    </AlertTitle>
                                                    {seatsResult.seatsCreated} seats,{' '}
                                                    {seatsResult.bookableSeats} bookable,{' '}
                                                    {seatsResult.decorativeElements} decorative,{' '}
                                                    {seatsResult.ticketTypeSeatLinks} type links
                                                    {seatsResult.errors?.length > 0 && (
                                                        <Box sx={{ mt: 1 }}>
                                                            {seatsResult.errors.map((err, i) => (
                                                                <Typography
                                                                    key={i}
                                                                    variant="body2"
                                                                    color="error"
                                                                >
                                                                    {err}
                                                                </Typography>
                                                            ))}
                                                        </Box>
                                                    )}
                                                </Alert>
                                            )}
                                        </Box>
                                    </AccordionDetails>
                                </Accordion>
                            )}

                            {/* Date range + Import options side by side */}
                            <Grid2 container spacing={2}>
                                <Grid2 size={{ xs: 12, md: 6 }}>
                                    <Card sx={{ height: '100%' }}>
                                        <CardContent>
                                            <Typography variant="h5" gutterBottom>
                                                Date Range
                                            </Typography>
                                            <Grid2 container spacing={2}>
                                                <Grid2 size={{ xs: 12, md: 6 }}>
                                                    <TextField
                                                        fullWidth
                                                        label="From"
                                                        type="date"
                                                        value={dateFrom}
                                                        onChange={(e) =>
                                                            setDateFrom(e.target.value)
                                                        }
                                                        slotProps={{ inputLabel: { shrink: true } }}
                                                    />
                                                </Grid2>
                                                <Grid2 size={{ xs: 12, md: 6 }}>
                                                    <TextField
                                                        fullWidth
                                                        label="To"
                                                        type="date"
                                                        value={dateTo}
                                                        onChange={(e) => setDateTo(e.target.value)}
                                                        slotProps={{ inputLabel: { shrink: true } }}
                                                    />
                                                </Grid2>
                                            </Grid2>
                                        </CardContent>
                                    </Card>
                                </Grid2>
                                <Grid2 size={{ xs: 12, md: 6 }}>
                                    <Card sx={{ height: '100%' }}>
                                        <CardContent>
                                            <Typography variant="h5" gutterBottom>
                                                Import Options
                                            </Typography>
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                }}
                                            >
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={sampleMode}
                                                            onChange={(e) =>
                                                                setSampleMode(e.target.checked)
                                                            }
                                                        />
                                                    }
                                                    label="Sample mode (fake data — no notifications)"
                                                />
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={createMissingTimeslots}
                                                            onChange={(e) =>
                                                                setCreateMissingTimeslots(
                                                                    e.target.checked,
                                                                )
                                                            }
                                                        />
                                                    }
                                                    label="Create missing timeslots"
                                                />
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={skipCancelled}
                                                            onChange={(e) => {
                                                                setSkipCancelled(e.target.checked);
                                                                if (
                                                                    e.target.checked &&
                                                                    skipRebooked
                                                                ) {
                                                                    setIncludeCancelledAvailabilities(
                                                                        false,
                                                                    );
                                                                }
                                                            }}
                                                        />
                                                    }
                                                    label="Skip cancelled bookings"
                                                />
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={skipRebooked}
                                                            onChange={(e) => {
                                                                setSkipRebooked(e.target.checked);
                                                                if (e.target.checked) {
                                                                    setResolveRebookedLinks(false);
                                                                    if (skipCancelled) {
                                                                        setIncludeCancelledAvailabilities(
                                                                            false,
                                                                        );
                                                                    }
                                                                }
                                                            }}
                                                        />
                                                    }
                                                    label="Skip rebooked bookings"
                                                />
                                                <FormControlLabel
                                                    disabled={skipRebooked}
                                                    control={
                                                        <Checkbox
                                                            checked={resolveRebookedLinks}
                                                            onChange={(e) =>
                                                                setResolveRebookedLinks(
                                                                    e.target.checked,
                                                                )
                                                            }
                                                        />
                                                    }
                                                    label="Resolve rebooked booking links"
                                                />
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={updateCommissionBillingType}
                                                            onChange={(e) =>
                                                                setUpdateCommissionBillingType(
                                                                    e.target.checked,
                                                                )
                                                            }
                                                        />
                                                    }
                                                    label="Update commission group billing type"
                                                />
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={createMissingPaymentMethods}
                                                            onChange={(e) =>
                                                                setCreateMissingPaymentMethods(
                                                                    e.target.checked,
                                                                )
                                                            }
                                                        />
                                                    }
                                                    label="Create missing payment methods"
                                                />
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={linkPaymentMethodsToGroups}
                                                            onChange={(e) =>
                                                                setLinkPaymentMethodsToGroups(
                                                                    e.target.checked,
                                                                )
                                                            }
                                                        />
                                                    }
                                                    label="Link payment methods to commission groups"
                                                />
                                                <FormControlLabel
                                                    disabled={skipCancelled && skipRebooked}
                                                    control={
                                                        <Checkbox
                                                            checked={includeCancelledAvailabilities}
                                                            onChange={(e) =>
                                                                setIncludeCancelledAvailabilities(
                                                                    e.target.checked,
                                                                )
                                                            }
                                                        />
                                                    }
                                                    label="Include cancelled availabilities"
                                                />
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={skipDryRun}
                                                            onChange={(e) =>
                                                                setSkipDryRun(e.target.checked)
                                                            }
                                                        />
                                                    }
                                                    label="Skip validation (dry run)"
                                                />
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Grid2>
                            </Grid2>

                            {/* Progress indicators for manifest + dry run */}
                            {loading && manifestProgress && (
                                <Card>
                                    <CardContent>
                                        <Typography variant="subtitle2" gutterBottom>
                                            Fetching manifest: {manifestProgress.label}
                                        </Typography>
                                        <LinearProgress
                                            variant="determinate"
                                            value={
                                                manifestProgress.total > 0
                                                    ? (manifestProgress.finished /
                                                          manifestProgress.total) *
                                                      100
                                                    : 0
                                            }
                                            sx={{ mb: 1 }}
                                        />
                                        <Typography variant="caption" color="text.secondary">
                                            {manifestProgress.finished} / {manifestProgress.total}{' '}
                                            months
                                        </Typography>
                                    </CardContent>
                                </Card>
                            )}
                            {loading && dryRunProgress && (
                                <Card>
                                    <CardContent>
                                        <Typography variant="subtitle2" gutterBottom>
                                            Dry run preview
                                        </Typography>
                                        <LinearProgress
                                            variant="determinate"
                                            value={
                                                dryRunProgress.total > 0
                                                    ? (dryRunProgress.finished /
                                                          dryRunProgress.total) *
                                                      100
                                                    : 0
                                            }
                                            sx={{ mb: 1 }}
                                        />
                                        <Typography variant="caption" color="text.secondary">
                                            {dryRunProgress.finished} / {dryRunProgress.total}{' '}
                                            bookings
                                        </Typography>
                                    </CardContent>
                                </Card>
                            )}

                            {renderStepNav(
                                2,
                                handleManifestAndPreview,
                                loading ? 'Fetching...' : 'Fetch Manifest & Preview',
                                loading || !dateFrom || !dateTo,
                            )}
                        </Box>
                    </Grid2>
                )}

                {/* Step 5: Manifest + Preview results */}
                {activeStep === 4 && manifestData && (
                    <Grid2 size={{ xs: 12 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            {/* Manifest summary */}
                            <Card>
                                <CardContent>
                                    <Typography variant="h5" gutterBottom>
                                        Manifest Summary
                                    </Typography>
                                    <Grid2 container spacing={2} sx={{ mb: 2 }}>
                                        {[
                                            { value: manifestData.totalBookings, label: 'Total' },
                                            {
                                                value: manifestData.alreadyImportedCount || 0,
                                                label: 'Will Overwrite',
                                                color: 'warning.main',
                                            },
                                            {
                                                value: manifestData.cancelledCount || 0,
                                                label: 'Cancelled',
                                                color: 'error.main',
                                            },
                                            {
                                                value: manifestData.rebookedCount || 0,
                                                label: 'Rebooked',
                                                color: 'warning.main',
                                            },
                                            {
                                                value: importableUuids.length,
                                                label: 'To Import',
                                                color: 'primary',
                                            },
                                        ].map((stat) => (
                                            <Grid2 key={stat.label} size={{ xs: 6, md: 2.4 }}>
                                                <Paper
                                                    variant="outlined"
                                                    sx={{
                                                        p: 2,
                                                        textAlign: 'center',
                                                        bgcolor: '#f5f5f5',
                                                    }}
                                                >
                                                    <Typography variant="h4" color={stat.color}>
                                                        {stat.value}
                                                    </Typography>
                                                    <Typography
                                                        variant="body2"
                                                        color="text.secondary"
                                                    >
                                                        {stat.label}
                                                    </Typography>
                                                </Paper>
                                            </Grid2>
                                        ))}
                                    </Grid2>
                                    {manifestData.rebookedCount > 0 && (
                                        <Alert severity="info" sx={{ mb: 1 }}>
                                            There is no rebooked status in Fireknife — rebooked
                                            bookings are imported as cancelled. The cancelled count
                                            will be higher than in FareHarbor because it includes
                                            both cancelled and rebooked bookings.
                                        </Alert>
                                    )}
                                    {manifestData.dates?.length > 0 && (
                                        <Box sx={{ mt: 1 }}>
                                            <Typography
                                                variant="subtitle2"
                                                sx={{ fontWeight: 'bold' }}
                                                gutterBottom
                                            >
                                                Bookings per Date
                                            </Typography>
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    flexWrap: 'wrap',
                                                    gap: 0.5,
                                                }}
                                            >
                                                {manifestData.dates.map((d) => (
                                                    <Chip
                                                        key={d.date}
                                                        label={`${d.date}: ${d.bookingCount}`}
                                                        size="small"
                                                        variant="outlined"
                                                        color={
                                                            d.bookingCount < 0 ? 'error' : 'default'
                                                        }
                                                    />
                                                ))}
                                            </Box>
                                        </Box>
                                    )}
                                </CardContent>
                            </Card>

                            {/* Preview results */}
                            {previewData && (
                                <Card>
                                    <CardContent>
                                        <Typography variant="h5" gutterBottom>
                                            Dry Run Results
                                        </Typography>
                                        <Grid2 container spacing={2} sx={{ mb: 3 }}>
                                            {[
                                                {
                                                    value: previewData.imported,
                                                    label: 'Would Import',
                                                    color: 'success.main',
                                                },
                                                { value: previewData.skipped, label: 'Skipped' },
                                                {
                                                    value: previewData.errors?.length || 0,
                                                    label: 'Errors',
                                                    color: 'error.main',
                                                },
                                                {
                                                    value: previewData.warnings?.length || 0,
                                                    label: 'Warnings',
                                                },
                                            ].map((stat) => (
                                                <Grid2 key={stat.label} size={{ xs: 6, md: 3 }}>
                                                    <Paper
                                                        variant="outlined"
                                                        sx={{
                                                            p: 2,
                                                            textAlign: 'center',
                                                            bgcolor: '#f5f5f5',
                                                        }}
                                                    >
                                                        <Typography variant="h4" color={stat.color}>
                                                            {stat.value}
                                                        </Typography>
                                                        <Typography variant="body2">
                                                            {stat.label}
                                                        </Typography>
                                                    </Paper>
                                                </Grid2>
                                            ))}
                                        </Grid2>

                                        {/* Financial summary */}
                                        {previewData.preview?.financialSummary && (
                                            <Typography variant="subtitle1" gutterBottom>
                                                Financial Summary
                                            </Typography>
                                        )}
                                        {previewData.preview?.financialSummary && (
                                            <Grid2 container spacing={2} sx={{ mb: 3 }}>
                                                {(() => {
                                                    const fs = previewData.preview.financialSummary;
                                                    return [
                                                        {
                                                            value: fmtUsd(fs.totalRevenue),
                                                            label: 'Revenue',
                                                        },
                                                        {
                                                            value: fmtUsd(
                                                                fs.totalRevenueCollected || 0,
                                                            ),
                                                            label: 'Revenue Collected',
                                                        },
                                                        {
                                                            value: fmtUsd(
                                                                fs.totalReceivableFromAffiliate ||
                                                                    0,
                                                            ),
                                                            label: 'Receivable from Affiliate',
                                                        },
                                                        {
                                                            value: fmtUsd(fs.totalCommission),
                                                            label: 'Commission',
                                                        },
                                                        {
                                                            value: fmtUsd(fs.totalProcessingFees),
                                                            label: 'Processing Fees',
                                                        },
                                                        {
                                                            value: fmtUsd(fs.totalTax || 0),
                                                            label: 'Tax',
                                                        },
                                                        {
                                                            value: fmtUsd(fs.netRevenue || 0),
                                                            label: 'Net Revenue',
                                                            color: 'primary',
                                                        },
                                                    ];
                                                })().map((stat, i, arr) => (
                                                    <Grid2
                                                        key={stat.label}
                                                        size={{
                                                            xs: i === arr.length - 1 ? 12 : 6,
                                                            md: i === arr.length - 1 ? 12 : 4,
                                                        }}
                                                    >
                                                        <Paper
                                                            variant="outlined"
                                                            sx={{
                                                                p: 2,
                                                                textAlign: 'center',
                                                                bgcolor: '#f5f5f5',
                                                            }}
                                                        >
                                                            <Typography
                                                                variant="h5"
                                                                color={stat.color}
                                                            >
                                                                {stat.value}
                                                            </Typography>
                                                            <Typography
                                                                variant="body2"
                                                                color="text.secondary"
                                                            >
                                                                {stat.label}
                                                            </Typography>
                                                        </Paper>
                                                    </Grid2>
                                                ))}
                                            </Grid2>
                                        )}

                                        {/* Financial breakdown by booking status */}
                                        {previewData.preview?.financialSummary &&
                                            (() => {
                                                const fs = previewData.preview.financialSummary;
                                                return fs.cancelledRevenue > 0 ||
                                                    fs.rebookedSkippedCount > 0 ? (
                                                    <Alert severity="info" sx={{ mb: 2 }}>
                                                        <Typography variant="body2">
                                                            <b>Active bookings:</b> Revenue{' '}
                                                            {fmtUsd(fs.activeRevenue)}, Commission{' '}
                                                            {fmtUsd(fs.activeCommission)}
                                                        </Typography>
                                                        {fs.cancelledRevenue > 0 && (
                                                            <Typography variant="body2">
                                                                <b>Cancelled bookings:</b> Revenue{' '}
                                                                {fmtUsd(fs.cancelledRevenue)},
                                                                Commission{' '}
                                                                {fmtUsd(fs.cancelledCommission)}
                                                            </Typography>
                                                        )}
                                                        {fs.rebookedSkippedCount > 0 && (
                                                            <Typography variant="body2">
                                                                <b>Rebooked:</b>{' '}
                                                                {fs.rebookedSkippedCount} booking(s)
                                                                excluded from financial totals
                                                            </Typography>
                                                        )}
                                                    </Alert>
                                                ) : null;
                                            })()}

                                        {/* Commission groups */}
                                        {previewData.preview?.commissionGroups?.filter(
                                            (g) => g.isNew,
                                        ).length > 0 && (
                                            <Box sx={{ mb: 2 }}>
                                                <Typography
                                                    variant="subtitle2"
                                                    sx={{ fontWeight: 'bold' }}
                                                >
                                                    Commission Groups to Create
                                                </Typography>
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        gap: 0.5,
                                                        flexWrap: 'wrap',
                                                        mt: 0.5,
                                                    }}
                                                >
                                                    {previewData.preview.commissionGroups
                                                        .filter((g) => g.isNew)
                                                        .map((g, i) => (
                                                            <Chip
                                                                key={i}
                                                                label={g.name}
                                                                size="small"
                                                            />
                                                        ))}
                                                </Box>
                                            </Box>
                                        )}

                                        {/* Agents */}
                                        {previewData.preview?.agents?.filter((a) => a.isNew)
                                            .length > 0 && (
                                            <Box sx={{ mb: 2 }}>
                                                <Typography
                                                    variant="subtitle2"
                                                    sx={{ fontWeight: 'bold' }}
                                                >
                                                    Agents to Create
                                                </Typography>
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        gap: 0.5,
                                                        flexWrap: 'wrap',
                                                        mt: 0.5,
                                                    }}
                                                >
                                                    {previewData.preview.agents
                                                        .filter((a) => a.isNew)
                                                        .map((a, i) => (
                                                            <Chip
                                                                key={i}
                                                                label={a.name}
                                                                size="small"
                                                            />
                                                        ))}
                                                </Box>
                                            </Box>
                                        )}

                                        {/* Timeslots */}
                                        {previewData.preview?.timeslotsToCreate?.length > 0 && (
                                            <Box sx={{ mb: 2 }}>
                                                <Typography
                                                    variant="subtitle2"
                                                    sx={{ fontWeight: 'bold' }}
                                                >
                                                    {previewData.preview.timeslotsToCreate.length}{' '}
                                                    Timeslot(s) to Create
                                                </Typography>
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        gap: 0.5,
                                                        flexWrap: 'wrap',
                                                        mt: 0.5,
                                                    }}
                                                >
                                                    {previewData.preview.timeslotsToCreate.map(
                                                        (ts, i) => (
                                                            <Chip
                                                                key={i}
                                                                label={ts
                                                                    .replace('T', ' ')
                                                                    .replace(/:00$/, '')}
                                                                size="small"
                                                                variant="outlined"
                                                            />
                                                        ),
                                                    )}
                                                </Box>
                                            </Box>
                                        )}

                                        {/* Errors */}
                                        {previewData.errors?.length > 0 && (
                                            <Alert severity="warning" sx={{ mb: 2 }}>
                                                <AlertTitle>
                                                    {previewData.errors.length} error(s)
                                                </AlertTitle>
                                                <List
                                                    dense
                                                    sx={{ maxHeight: 200, overflow: 'auto' }}
                                                >
                                                    {previewData.errors.map((err, i) => (
                                                        <ListItem key={i}>
                                                            <ListItemText
                                                                primary={err.bookingId}
                                                                secondary={err.error}
                                                            />
                                                        </ListItem>
                                                    ))}
                                                </List>
                                            </Alert>
                                        )}

                                        {/* Warnings */}
                                        {previewData.warnings?.length > 0 && (
                                            <Alert severity="info" sx={{ mb: 2 }}>
                                                <AlertTitle>
                                                    {previewData.warnings.length} warning(s)
                                                </AlertTitle>
                                                <List
                                                    dense
                                                    sx={{ maxHeight: 200, overflow: 'auto' }}
                                                >
                                                    {previewData.warnings.map((w, i) => (
                                                        <ListItem key={i}>
                                                            <ListItemText
                                                                primary={w.bookingId}
                                                                secondary={w.warning}
                                                            />
                                                        </ListItem>
                                                    ))}
                                                </List>
                                            </Alert>
                                        )}
                                    </CardContent>
                                </Card>
                            )}

                            {!previewData && importableUuids.length === 0 && (
                                <Alert severity="info">No bookings to import in this range.</Alert>
                            )}

                            {(manifestData.alreadyImportedCount || 0) > 0 && (
                                <Alert severity="warning">
                                    {manifestData.alreadyImportedCount} booking(s) in this range
                                    were previously imported. Submitting will delete those bookings
                                    and their orders, items, and transactions, then re-create them
                                    from the latest FareHarbor data.
                                    <Box component="span" sx={{ display: 'block', mt: 1 }}>
                                        <strong>Heads up:</strong> any live modifications made in
                                        Fireknife that have not yet been pushed back to FareHarbor
                                        will be overwritten by this import and may resurface as
                                        duplicate-looking activity once both sides re-sync.
                                    </Box>
                                </Alert>
                            )}

                            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                                <CommonButton
                                    onClick={() => setActiveStep(3)}
                                    label="Back"
                                    buttonType="secondary"
                                />
                                <CommonButton
                                    onClick={handleExecuteImport}
                                    disabled={loading || importableUuids.length === 0}
                                    label={
                                        loading
                                            ? 'Importing...'
                                            : `Execute Import (${importableUuids.length} bookings)`
                                    }
                                    buttonType="primary"
                                />
                            </Box>
                        </Box>
                    </Grid2>
                )}

                {/* Step 6: Import execution */}
                {activeStep === 5 && (
                    <Grid2 size={{ xs: 12 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h5" gutterBottom>
                                        Import Progress
                                    </Typography>
                                    {importProgress && (
                                        <Box sx={{ mb: 3 }}>
                                            <LinearProgress
                                                variant="determinate"
                                                value={
                                                    importProgress.total > 0
                                                        ? (importProgress.finished /
                                                              importProgress.total) *
                                                          100
                                                        : 0
                                                }
                                                sx={{ height: 10, borderRadius: 5 }}
                                            />
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                sx={{ mt: 1 }}
                                            >
                                                Processed {importProgress.finished} of{' '}
                                                {importProgress.total} bookings (
                                                {importProgress.imported} imported)
                                            </Typography>
                                        </Box>
                                    )}

                                    {importErrors.length > 0 && (
                                        <Alert severity="warning" sx={{ mb: 2 }}>
                                            <AlertTitle>{importErrors.length} error(s)</AlertTitle>
                                            <List dense sx={{ maxHeight: 300, overflow: 'auto' }}>
                                                {importErrors.map((err, i) => (
                                                    <ListItem key={i}>
                                                        <ListItemText
                                                            primary={err.bookingId}
                                                            secondary={err.error}
                                                        />
                                                    </ListItem>
                                                ))}
                                            </List>
                                        </Alert>
                                    )}

                                    {!loading &&
                                        importProgress &&
                                        importProgress.finished >= importProgress.total && (
                                            <Alert severity="success">
                                                Import complete! {importProgress.imported} bookings
                                                imported.
                                            </Alert>
                                        )}
                                </CardContent>
                            </Card>
                            {!loading && (
                                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                                    <CommonButton
                                        onClick={() => setActiveStep(6)}
                                        label="Go to Verify"
                                        buttonType="primary"
                                    />
                                    <CommonButton
                                        onClick={() => navigate('/venues')}
                                        label="Go to Venues"
                                        buttonType="secondary"
                                    />
                                </Box>
                            )}
                        </Box>
                    </Grid2>
                )}

                {/* Step 6: Verify */}
                {activeStep === 6 && (
                    <Grid2 size={{ xs: 12 }}>
                        <Card>
                            <CardContent>
                                <Typography variant="h5" gutterBottom>
                                    Verify Import
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                    Upload a FareHarbor CSV export to verify imported booking data
                                    matches the source.
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    To export from FareHarbor: go to{' '}
                                    <strong>Reports &rarr; Advanced &rarr; Bookings</strong>. Set{' '}
                                    <strong>Dates</strong> to Custom Range covering all bookings
                                    (e.g. 01/01/2015 &ndash; 01/01/2027). Leave{' '}
                                    <strong>Filter</strong> and <strong>Group by</strong> empty.
                                    Under <strong>Columns</strong> select{' '}
                                    <strong>Detailed report</strong> and select <strong>all</strong>{' '}
                                    Booking columns. Click Generate, then download as CSV.
                                </Typography>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: { xs: 'column', sm: 'row' },
                                        gap: 2,
                                        alignItems: { xs: 'stretch', sm: 'center' },
                                        mb: 2,
                                    }}
                                >
                                    <Button
                                        variant="outlined"
                                        component="label"
                                        disabled={verifyLoading}
                                    >
                                        {verifyLoading ? 'Verifying...' : 'Upload CSV & Verify'}
                                        <input
                                            type="file"
                                            accept=".csv"
                                            hidden
                                            onChange={async (e) => {
                                                const file = e.target.files?.[0];
                                                if (!file) return;
                                                setVerifyLoading(true);
                                                setVerifyResult(null);
                                                try {
                                                    const csvContent = await file.text();
                                                    const result = await fhDirectVerify({
                                                        showId,
                                                        csvContent,
                                                        includeAllDetails: verifyIncludeAll,
                                                    });
                                                    setVerifyResult(result);
                                                } catch (err) {
                                                    notifyError(
                                                        err?.message || 'Verification failed',
                                                    );
                                                }
                                                setVerifyLoading(false);
                                                e.target.value = '';
                                            }}
                                        />
                                    </Button>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={verifyIncludeAll}
                                                onChange={(e) =>
                                                    setVerifyIncludeAll(e.target.checked)
                                                }
                                            />
                                        }
                                        label="Show all bookings (including passed)"
                                    />
                                </Box>

                                {verifyLoading && (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            mb: 2,
                                        }}
                                    >
                                        <CircularProgress size={20} />
                                        <Typography variant="body2">
                                            Running verification...
                                        </Typography>
                                    </Box>
                                )}

                                {verifyResult && (
                                    <Box>
                                        {verifyResult.missingColumns?.length > 0 && (
                                            <Alert severity="warning" sx={{ mb: 2 }}>
                                                <AlertTitle>Missing CSV columns</AlertTitle>
                                                The following columns are missing from the CSV and
                                                their checks will be skipped:{' '}
                                                <strong>
                                                    {verifyResult.missingColumns.join(', ')}
                                                </strong>
                                                . Make sure to select <strong>all</strong> Booking
                                                columns in the FareHarbor report.
                                            </Alert>
                                        )}
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                gap: 2,
                                                mb: 2,
                                                flexWrap: 'wrap',
                                            }}
                                        >
                                            <Chip
                                                label={`Passed: ${verifyResult.passed}`}
                                                color="success"
                                            />
                                            <Chip
                                                label={`Failed: ${verifyResult.failed}`}
                                                color={
                                                    verifyResult.failed > 0 ? 'error' : 'default'
                                                }
                                            />
                                            <Chip
                                                label={`Not imported: ${verifyResult.notImported}`}
                                                color="default"
                                            />
                                            <Chip
                                                label={`CSV: ${verifyResult.totalCsv}`}
                                                variant="outlined"
                                            />
                                            <Chip
                                                label={`DB: ${verifyResult.totalDb}`}
                                                variant="outlined"
                                            />
                                        </Box>

                                        {verifyResult.failed === 0 && (
                                            <Alert severity="success" sx={{ mb: 2 }}>
                                                All bookings verified successfully!
                                            </Alert>
                                        )}

                                        {verifyResult.details?.length > 0 && (
                                            <Accordion defaultExpanded>
                                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                                    <Typography>
                                                        {verifyResult.details.length} booking(s)
                                                        {verifyIncludeAll ? '' : ' with issues'}
                                                    </Typography>
                                                </AccordionSummary>
                                                <AccordionDetails>
                                                    <TableContainer
                                                        component={Paper}
                                                        sx={{ maxHeight: 600 }}
                                                    >
                                                        <Table size="small" stickyHeader>
                                                            <TableHead>
                                                                <TableRow>
                                                                    <TableCell>Booking</TableCell>
                                                                    <TableCell>Result</TableCell>
                                                                    <TableCell>DB Status</TableCell>
                                                                    <TableCell>Affiliate</TableCell>
                                                                    <TableCell align="right">
                                                                        CSV Total
                                                                    </TableCell>
                                                                    <TableCell align="right">
                                                                        DB Total
                                                                    </TableCell>
                                                                    <TableCell align="right">
                                                                        Pax
                                                                    </TableCell>
                                                                    <TableCell align="right">
                                                                        CSV Commission
                                                                    </TableCell>
                                                                    <TableCell align="right">
                                                                        DB Commission
                                                                    </TableCell>
                                                                    <TableCell align="right">
                                                                        Orders
                                                                    </TableCell>
                                                                    <TableCell>Issues</TableCell>
                                                                </TableRow>
                                                            </TableHead>
                                                            <TableBody>
                                                                {verifyResult.details.map((d) => (
                                                                    <TableRow
                                                                        key={d.code}
                                                                        sx={{
                                                                            backgroundColor:
                                                                                d.status ===
                                                                                'failed'
                                                                                    ? 'error.light'
                                                                                    : undefined,
                                                                        }}
                                                                    >
                                                                        <TableCell>
                                                                            {d.code}
                                                                        </TableCell>
                                                                        <TableCell>
                                                                            <Chip
                                                                                label={d.status}
                                                                                color={
                                                                                    d.status ===
                                                                                    'passed'
                                                                                        ? 'success'
                                                                                        : 'error'
                                                                                }
                                                                                size="small"
                                                                            />
                                                                        </TableCell>
                                                                        <TableCell>
                                                                            {d.values?.dbStatus ||
                                                                                '-'}
                                                                        </TableCell>
                                                                        <TableCell>
                                                                            {d.values?.affiliate ||
                                                                                '-'}
                                                                        </TableCell>
                                                                        <TableCell align="right">
                                                                            {d.values
                                                                                ? formatCentsToUsd(
                                                                                      d.values
                                                                                          .csvTotal,
                                                                                  )
                                                                                : '-'}
                                                                        </TableCell>
                                                                        <TableCell align="right">
                                                                            {d.values
                                                                                ? formatCentsToUsd(
                                                                                      d.values
                                                                                          .dbTotal,
                                                                                  )
                                                                                : '-'}
                                                                        </TableCell>
                                                                        <TableCell align="right">
                                                                            {d.values
                                                                                ? d.values.dbPax
                                                                                : '-'}
                                                                        </TableCell>
                                                                        <TableCell align="right">
                                                                            {d.values
                                                                                ? formatCentsToUsd(
                                                                                      d.values
                                                                                          .csvCommission,
                                                                                  )
                                                                                : '-'}
                                                                        </TableCell>
                                                                        <TableCell align="right">
                                                                            {d.values
                                                                                ? formatCentsToUsd(
                                                                                      d.values
                                                                                          .dbCommission,
                                                                                  )
                                                                                : '-'}
                                                                        </TableCell>
                                                                        <TableCell align="right">
                                                                            {d.values?.orders ??
                                                                                '-'}
                                                                        </TableCell>
                                                                        <TableCell>
                                                                            {d.checks.length > 0
                                                                                ? d.checks
                                                                                      .map(
                                                                                          (c) =>
                                                                                              `${c.type}: ${c.expected} → ${c.actual}`,
                                                                                      )
                                                                                      .join(', ')
                                                                                : '-'}
                                                                        </TableCell>
                                                                    </TableRow>
                                                                ))}
                                                            </TableBody>
                                                        </Table>
                                                    </TableContainer>
                                                </AccordionDetails>
                                            </Accordion>
                                        )}

                                        {verifyResult.details?.some((d) =>
                                            d.checks?.some((c) => c.fixable),
                                        ) && (
                                            <Box sx={{ mt: 2 }}>
                                                <CommonButton
                                                    onClick={async () => {
                                                        setFixLoading(true);
                                                        try {
                                                            const fixItems = [];
                                                            for (const d of verifyResult.details) {
                                                                for (const c of d.checks || []) {
                                                                    if (c.fixable) {
                                                                        fixItems.push({
                                                                            code: d.code,
                                                                            type: c.type,
                                                                            expected: c.expected,
                                                                        });
                                                                    }
                                                                }
                                                            }
                                                            const fixResult =
                                                                await fhDirectVerifyFix({
                                                                    showId,
                                                                    fixes: fixItems,
                                                                });
                                                            if (fixResult.errors?.length > 0) {
                                                                notifyError(
                                                                    `Fixed ${fixResult.totalFixes} issue(s) with ${fixResult.errors.length} error(s).`,
                                                                );
                                                            } else {
                                                                notifySuccess(
                                                                    `Fixed ${fixResult.totalFixes} issue(s).`,
                                                                );
                                                            }
                                                            setVerifyResult(null);
                                                        } catch (err) {
                                                            notifyError(
                                                                err?.message || 'Fix failed',
                                                            );
                                                        }
                                                        setFixLoading(false);
                                                    }}
                                                    label={
                                                        fixLoading
                                                            ? 'Fixing...'
                                                            : `Fix ${verifyResult.details.reduce((s, d) => s + (d.checks || []).filter((c) => c.fixable).length, 0)} fixable issue(s)`
                                                    }
                                                    buttonType="warning"
                                                    disabled={fixLoading}
                                                />
                                            </Box>
                                        )}
                                    </Box>
                                )}
                            </CardContent>
                        </Card>
                        <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
                            <CommonButton
                                onClick={() => navigate('/venues')}
                                label="Go to Venues"
                                buttonType="primary"
                            />
                        </Box>
                    </Grid2>
                )}
            </PageWithTitle>
        </Box>
    );
}

function Loader() {
    return (
        <Grid2 size={{ xs: 12 }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size="1.5rem" />
            </Box>
        </Grid2>
    );
}

export default ImportVenuePage;
